import React, { useState, useEffect } from 'react';
import { BlockContent, TextBlockPayload, ProductListPayload, MessageButton, CommandVariable, ProductListItem } from '../../../../types/chat-message';
import './ProductListCard.scss';
import { EmptyProductIcon, HetHangIcon } from '@assets/icon';
import { chatService } from '@services/chatService';
import { safePostMessage } from '@utils/index';

// Helper to check if button should be hidden
const checkIsHidden = (commandVariables?: CommandVariable[]): boolean => {
    if (!commandVariables) return false;
    const hiddenVar = commandVariables.find(v => v.name === 'hidden_message');
    if (!hiddenVar) return false;
    return hiddenVar.value === 'true';
};

// Helper function to render text with **bold**, \n newlines
const renderFormattedText = (text: string): React.ReactNode => {
    if (!text) return null;
    const lines = text.split('\n');
    return (
        <>
            {lines.map((line, lineIndex) => {
                if (line.trim() === '') {
                    return <div key={lineIndex} style={{ height: '8px' }} />;
                }
                const parts = line.split(/(\*\*.*?\*\*)/g);
                return (
                    <div key={lineIndex}>
                        {parts.map((part, idx) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={idx}>{part.slice(2, -2)}</strong>;
                            }
                            return <span key={idx}>{part}</span>;
                        })}
                    </div>
                );
            })}
        </>
    );
};

interface ProductListCardProps {
    blockContents: BlockContent[];
    onButtonClick?: (name: string, id: number, isHidden?: boolean) => void;
    onThinkingStop?: () => void;
    isLastMessage?: boolean;
    isFromHistory?: boolean;
    messageId?: string | number;
    messageIdStr?: string;
}

const EMPTY_PRODUCTS: ProductListItem[] = [];

const ProductListCard: React.FC<ProductListCardProps> = ({
    blockContents,
    onButtonClick,
    onThinkingStop,
    isLastMessage,
    isFromHistory,
    messageId,
    messageIdStr
}) => {
    const isDisabled = isFromHistory || !isLastMessage;

    const textBlock = blockContents.find(b => b.type === 'TEXT');
    const textPayload = textBlock?.payload as TextBlockPayload | undefined;

    const listBlock = blockContents.find(b => b.type === 'PRODUCT_LIST');
    const listPayload = listBlock?.payload as ProductListPayload | undefined;
    const products = listPayload?.products || EMPTY_PRODUCTS;

    const [visibleCount, setVisibleCount] = useState(3);
    const visibleProducts = products.slice(0, visibleCount);
    const hasMore = visibleCount < products.length;
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Helper to generate a unique key for product
    const getProductKey = (product: any, index: number) => {
        if (product.productId && product.productCode) {
            return `${product.productId}_${product.productCode}`;
        }
        return product.productId || product.productCode || `product-${index}`;
    };

    // Calculate which products are out of stock
    const getProductOutOfStockStatus = (product: ProductListItem) => {
        if (!product) return false;
        return product.outofstock === true || product.outOfStock === true;
    };

    // State for selected product IDs/Keys
    const [selectedIds, setSelectedIds] = useState<string[]>(() => {
        return products
            .filter(p => p.addToCart === true && (isFromHistory || !getProductOutOfStockStatus(p)))
            .map((p, idx) => getProductKey(p, idx));
    });

    const displayCount = isDisabled
        ? products.filter(p => p.addToCart === true).length
        : selectedIds.length;

    // Đồng bộ selectedIds khi danh sách sản phẩm thay đổi
    useEffect(() => {
        const nextSelectedIds = products
            .filter(p => p.addToCart === true && (isFromHistory || !getProductOutOfStockStatus(p)))
            .map((p, idx) => getProductKey(p, idx));

        setSelectedIds(prev => {
            if (
                prev.length === nextSelectedIds.length &&
                prev.every((id, index) => id === nextSelectedIds[index])
            ) {
                return prev;
            }
            return nextSelectedIds;
        });
    }, [products, isFromHistory]);

    const handleToggleSelect = (key: string, product: any) => {
        const isCurrentlyChecked = selectedIds.includes(key);
        const targetAddToCart = !isCurrentlyChecked;

        setSelectedIds(prev => {
            if (prev.includes(key)) {
                return prev.filter(id => id !== key);
            } else {
                return [...prev, key];
            }
        });
        // Cập nhật trường addtocart dựa theo productid và productcode
        product.addToCart = targetAddToCart;
    };

    const handleCheckout = () => {
        const selectedProducts = products.filter((p, idx) => selectedIds.includes(getProductKey(p, idx)));
        if (selectedProducts.length === 0) return;

        // Mark as submitted to disable checkboxes and button
        setIsSubmitted(true);

        const token = sessionStorage.getItem('access_token');
        const activeMessageId = messageIdStr || messageId;
        if (token && activeMessageId) {
            const apiProducts = selectedProducts.map(p => ({
                productId: p.productId || '',
                productCode: p.productCode || ''
            }));
            chatService.addToCart(token, activeMessageId, apiProducts)
                .catch(err => {
                    console.error('[ProductListCard] Failed to add to cart on checkout:', err);
                });
        }

        const selectedProductDetails = selectedProducts.map(p => {
            if (p.productDetail && typeof p.productDetail === 'string') {
                try {
                    return JSON.parse(p.productDetail);
                } catch (e) {
                    console.error('[ProductListCard] Failed to parse productDetail:', e);
                    return p;
                }
            }
            return p.productDetail || p;
        });

        // Gửi postMessage đồng bộ thông tin sản phẩm đã chọn sang web cha
        safePostMessage({
            eventName: 'SYNC_PRODUCTS_TO_CART',
            products: selectedProductDetails
        });
        console.log('Đã gửi event SYNC_PRODUCTS_TO_CART', selectedProductDetails);
    };

    const handleLoadMore = () => {
        setVisibleCount(prev => prev + 10);
    };

    if (products.length === 0 && !textPayload?.text) return null;

    return (
        <div className="product-list-container">
            {textPayload?.text && (
                <div className="product-list-text">
                    {renderFormattedText(textPayload.text)}
                </div>
            )}

            <div className="product-list-card-wrapper">
                <div className="product-list-header">Sản phẩm</div>

                {products.length > 0 && (
                    <div className="product-list-cards">
                        {visibleProducts.map((product, idx) => {
                            const key = getProductKey(product, idx);
                            const isQuantityZero = product.quantity === 0 || product.quantity === '0';
                            const hasValidQuantity = product.quantity !== null && product.quantity !== undefined && product.quantity !== '' && !isQuantityZero && product.quantity !== 'Hết hàng';
                            
                            const isOutOfStock = getProductOutOfStockStatus(product);
                            const isChecked = isDisabled ? product.addToCart === true : selectedIds.includes(key);

                            let displayQuantity: string | number = product.quantity ?? '';
                            displayQuantity = product.quantity === null || product.quantity === undefined || product.quantity === '' ? 0 : product.quantity;
                            const isCheckboxDisabled = isOutOfStock || isDisabled || isSubmitted;

                            return (
                                <div key={key} className="product-row">
                                    <div 
                                        className={`custom-checkbox ${isChecked ? 'checked' : ''} ${isCheckboxDisabled ? 'disabled' : ''}`}
                                        onClick={() => !isCheckboxDisabled && handleToggleSelect(key, product)}
                                    >
                                        {isChecked && (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="check-icon">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        )}
                                    </div>
                                    <div className="product-item">
                                        <div className="product-image-container">
                                            {product.image ? (
                                                <img src={product.image} alt={product.name || 'Product'} className="product-image" />
                                            ) : (
                                                <div className="empty-image-icon">
                                                    <EmptyProductIcon />
                                                </div>
                                            )}
                                            {isOutOfStock && (
                                                <div className="out-of-stock-overlay">
                                                    <HetHangIcon />
                                                </div>
                                            )}
                                        </div>
                                        <div className="product-info-middle">
                                            <span className="product-title" title={product.name}>{product.name}</span>
                                            <span className="product-code">{product.productCode}</span>
                                            {product.category && (
                                                <div className="category-pill" title={`Phân loại: ${product.category}`}>
                                                    Phân loại: {product.category}
                                                </div>
                                            )}
                                        </div>
                                        <div className="product-info-right">
                                            <span className="product-price">{product.price}</span>
                                            <span className="product-quantity">
                                                {displayQuantity !== '' && displayQuantity !== null ? `Tồn kho: ${displayQuantity}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {hasMore && (
                    <div className="load-more-container">
                        <button className="btn-load-more" onClick={handleLoadMore}>
                            Xem thêm
                            <svg className="chevron-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                        </button>
                    </div>
                )}

                <div className="checkout-container">
                    <button 
                        className="btn-checkout" 
                        onClick={handleCheckout}
                        disabled={isDisabled || selectedIds.length === 0 || isSubmitted}
                    >
                        {displayCount > 0 ? `Bán hàng ngay (${displayCount})` : 'Bán hàng ngay'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProductListCard;
